import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Edit from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  LOAD_CLIENT_CREDIT_CARDS,
  loadClientCreditCards,
  loadClientBankAccountInformation
} from 'src/store/actions/clientActions';
import { getContractPendingAutomaticDebits } from 'src/services/paymentService';
import {
  UPDATE_CONTRACT_PAYMENT_METHOD,
  updateContractPaymentMethodAction
} from 'src/store/actions/contractActions';
import {
  selectClientCreditCards,
  selectClientBankAccountDetail
} from 'src/store/selectors/clientSelectors';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import ContractStatus, {
  ContractPaymentForm
} from 'src/utils/constants/contracts';
import {
  hasPaymentDetailsToShow,
  getPaymentMethodDisplayText
} from 'src/utils/paymentMethodHelpers';
import {
  shouldAutoSelectBankAccount,
  getFirstBankAccountId,
  getFirstCreditCardId,
  getInitialBankAccountIdForDialog
} from 'src/utils/paymentMethodAutoSelection';
import useStyles from '../../styles';
import PaymentMethodDetailsDialog from './PaymentMethodDetailsDialog';
import PaymentMethodEditDialog from './PaymentMethodEditDialog';

const PaymentMethodSelector = ({ contract }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Dialog states
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [hasPendingAutomaticDebits, setHasPendingAutomaticDebits] = useState(false);

  const [paymentForm, setPaymentForm] = useState(null);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState(null);
  const [
    selectedSecondaryBankAccountId,
    setSelectedSecondaryBankAccountId
  ] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  // Local state for dialog (to allow canceling changes)
  const [dialogPaymentForm, setDialogPaymentForm] = useState(null);
  const [dialogBankAccountId, setDialogBankAccountId] = useState(null);
  const [
    dialogSecondaryBankAccountId,
    setDialogSecondaryBankAccountId
  ] = useState(null);
  const [dialogCardId, setDialogCardId] = useState(null);

  const bankAccountDetail = useSelector(selectClientBankAccountDetail);
  const bankAccountsRaw = bankAccountDetail?.bankAccounts;
  const bankAccounts = Array.isArray(bankAccountsRaw) ? bankAccountsRaw : [];
  const creditCards = useSelector(selectClientCreditCards);
  const loadingCards = useSelector(
    isLoadingSelector([LOAD_CLIENT_CREDIT_CARDS.action])
  );
  const isSaving = useSelector(
    isLoadingSelector([UPDATE_CONTRACT_PAYMENT_METHOD.action])
  );

  // Determine if payment method can be edited based on contract status
  const isEditable = useMemo(() => {
    return (
      contract.status === ContractStatus.DRAFT ||
      contract.status === ContractStatus.PENDING_SIGNATURE ||
      contract.status === ContractStatus.IN_PROGRESS
    );
  }, [contract.status]);

  // Load credit cards and bank accounts on mount
  useEffect(() => {
    if (contract.clientId) {
      dispatch(loadClientCreditCards(contract.clientId));
      dispatch(loadClientBankAccountInformation(contract.clientId));
    }
  }, [contract.clientId, dispatch]);

  const validBankAccounts = useMemo(() => {
    return bankAccounts.filter(account => {
      return account.bankAccountNumber || account.accountNumber;
    });
  }, [bankAccounts]);

  const selectedBankAccount = useMemo(
    () => bankAccounts.find(acc => acc.id === selectedBankAccountId),
    [bankAccounts, selectedBankAccountId]
  );

  const selectedSecondaryBankAccount = useMemo(
    () => bankAccounts.find(acc => acc.id === selectedSecondaryBankAccountId),
    [bankAccounts, selectedSecondaryBankAccountId]
  );

  // Get selected credit card
  const selectedCard = useMemo(
    () => creditCards.find(card => card.id === selectedCardId),
    [creditCards, selectedCardId]
  );

  const showDetailsButton = hasPaymentDetailsToShow({
    paymentForm,
    selectedBankAccount,
    selectedCard,
    clabe: contract?.clabe
  });

  // Load data on mount
  useEffect(() => {
    if (contract.clientId) {
      dispatch(loadClientCreditCards(contract.clientId));
      dispatch(loadClientBankAccountInformation(contract.clientId));
    }
  }, [contract.clientId, dispatch]);

  useEffect(() => {
    if (contract) {
      if (contract.paymentForm) {
        setPaymentForm(contract.paymentForm);
      }

      setSelectedBankAccountId(
        contract.bankAccountId ? Number(contract.bankAccountId) : null
      );
      setSelectedSecondaryBankAccountId(
        contract.secondaryBankAccountId
          ? Number(contract.secondaryBankAccountId)
          : null
      );
      setSelectedCardId(contract.cardId ? Number(contract.cardId) : null);
    }
  }, [
    contract?.paymentForm,
    contract?.bankAccountId,
    contract?.secondaryBankAccountId,
    contract?.cardId,
    bankAccounts.length
  ]);

  // Auto-select bank account when payment form is AUTOMATIC_DEBIT and bank accounts are available
  useEffect(() => {
    if (
      shouldAutoSelectBankAccount({
        paymentForm,
        selectedBankAccountId,
        bankAccounts: validBankAccounts,
        contract,
        paymentFormType: ContractPaymentForm.AUTOMATIC_DEBIT
      })
    ) {
      const firstAccountId = getFirstBankAccountId(validBankAccounts);
      if (firstAccountId) {
        setSelectedBankAccountId(firstAccountId);
      }
    }
  }, [paymentForm, selectedBankAccountId, validBankAccounts, contract]);

  // Also auto-select in dialog when bank accounts become available
  useEffect(() => {
    if (
      openEditDialog &&
      shouldAutoSelectBankAccount({
        paymentForm: dialogPaymentForm,
        selectedBankAccountId: dialogBankAccountId,
        bankAccounts: validBankAccounts,
        paymentFormType: ContractPaymentForm.AUTOMATIC_DEBIT
      })
    ) {
      const firstAccountId = getFirstBankAccountId(validBankAccounts);
      if (firstAccountId) {
        setDialogBankAccountId(firstAccountId);
      }
    }
  }, [
    openEditDialog,
    dialogPaymentForm,
    dialogBankAccountId,
    validBankAccounts
  ]);

  // Note: We don't set default payment methods automatically anymore
  // If all properties are null, we show "Ningún método de pago ha sido elegido"

  // Dialog handlers
  const handleDialogPaymentFormChange = useCallback(
    value => {
      setDialogPaymentForm(value);
      // Reset selections when switching payment forms
      if (value === ContractPaymentForm.AUTOMATIC_DEBIT) {
        setDialogCardId(null);
        // Always set bank account when switching to AUTOMATIC_DEBIT
        setDialogBankAccountId(getFirstBankAccountId(validBankAccounts));
        setDialogSecondaryBankAccountId(null);
      } else if (value === ContractPaymentForm.CREDIT_CARD) {
        setDialogBankAccountId(null);
        setDialogSecondaryBankAccountId(null);
        setDialogCardId(getFirstCreditCardId(creditCards));
      } else if (value === ContractPaymentForm.BANK_TRANSFER) {
        setDialogBankAccountId(null);
        setDialogSecondaryBankAccountId(null);
        setDialogCardId(null);
      }
    },
    [validBankAccounts, creditCards]
  );

  const handleDialogBankAccountChange = useCallback(
    value => {
      setDialogBankAccountId(value);
      // reset the secondary bank account if its the same as the new primary
      if (value === dialogSecondaryBankAccountId)
        setDialogSecondaryBankAccountId(null);
    },
    [dialogSecondaryBankAccountId]
  );

  const handleDialogSecondaryBankAccountChange = useCallback(value => {
    setDialogSecondaryBankAccountId(value);
  }, []);

  const handleDialogCardChange = useCallback(value => {
    setDialogCardId(value);
  }, []);

  const handleOpenEditDialog = useCallback(async () => {
    if (contract?.clientId) {
      dispatch(loadClientBankAccountInformation(contract.clientId));
      dispatch(loadClientCreditCards(contract.clientId));
    }

    if (paymentForm === ContractPaymentForm.AUTOMATIC_DEBIT && contract?.id) {
      try {
        const result = await getContractPendingAutomaticDebits(contract.id);
        setHasPendingAutomaticDebits((result?.count ?? 0) > 0);
      } catch {
        setHasPendingAutomaticDebits(false);
      }
    } else {
      setHasPendingAutomaticDebits(false);
    }

    // Initialize dialog values from current state
    setDialogPaymentForm(paymentForm);

    const initialBankAccountId = getInitialBankAccountIdForDialog({
      paymentForm,
      paymentFormType: ContractPaymentForm.AUTOMATIC_DEBIT,
      selectedBankAccountId,
      validBankAccounts
    });

    setDialogBankAccountId(initialBankAccountId);
    setDialogSecondaryBankAccountId(selectedSecondaryBankAccountId);
    setDialogCardId(selectedCardId);
    setOpenEditDialog(true);
  }, [
    contract?.clientId,
    contract?.id,
    paymentForm,
    selectedBankAccountId,
    selectedSecondaryBankAccountId,
    selectedCardId,
    validBankAccounts
  ]);

  const handleCloseEditDialog = useCallback(() => setOpenEditDialog(false), []);
  const handleOpenDetailsDialog = useCallback(
    () => setOpenDetailsDialog(true),
    []
  );
  const handleCloseDetailsDialog = useCallback(
    () => setOpenDetailsDialog(false),
    []
  );

  const handleSave = useCallback(() => {
    if (!isEditable) return;

    const updateData = {
      paymentForm: dialogPaymentForm
    };

    // Add bank account or credit card ID based on payment form
    if (dialogPaymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
      if (dialogBankAccountId) {
        updateData.bankAccountId = dialogBankAccountId;
        updateData.secondaryBankAccountId =
          dialogSecondaryBankAccountId || null;
        updateData.cardId = null;
      }
    } else if (dialogPaymentForm === ContractPaymentForm.CREDIT_CARD) {
      if (dialogCardId) {
        updateData.cardId = dialogCardId;
        updateData.bankAccountId = null;
        updateData.secondaryBankAccountId = null;
      }
    } else if (dialogPaymentForm === ContractPaymentForm.BANK_TRANSFER) {
      updateData.bankAccountId = null;
      updateData.secondaryBankAccountId = null;
      updateData.cardId = null;
    }

    dispatch(
      updateContractPaymentMethodAction(contract.id, updateData, () => {
        // Update local state on success
        setPaymentForm(dialogPaymentForm);
        setSelectedBankAccountId(dialogBankAccountId);
        setSelectedSecondaryBankAccountId(dialogSecondaryBankAccountId);
        setSelectedCardId(dialogCardId);
        handleCloseEditDialog();
      })
    );
  }, [
    isEditable,
    dialogPaymentForm,
    dialogBankAccountId,
    dialogSecondaryBankAccountId,
    dialogCardId,
    contract?.id,
    dispatch,
    handleCloseEditDialog
  ]);

  // Validate if save button should be enabled
  const canSave = useMemo(() => {
    if (!isEditable) return false;

    if (dialogPaymentForm === ContractPaymentForm.AUTOMATIC_DEBIT) {
      return !!dialogBankAccountId;
    }
    if (dialogPaymentForm === ContractPaymentForm.CREDIT_CARD) {
      return creditCards.length > 0 && !!dialogCardId;
    }
    if (dialogPaymentForm === ContractPaymentForm.BANK_TRANSFER) {
      return !!contract?.clabe; // Transfer requires CLABE
    }
    return false;
  }, [
    isEditable,
    dialogPaymentForm,
    dialogBankAccountId,
    dialogCardId,
    contract?.clabe,
    creditCards.length
  ]);

  // Get payment method icon component for the icon box
  const getPaymentMethodIconComponent = () => {
    if (!paymentForm) {
      return null;
    }

    switch (paymentForm) {
      case ContractPaymentForm.AUTOMATIC_DEBIT:
        return (
          <AccountBalanceIcon sx={{ fontSize: 24, color: 'primary.main' }} />
        );
      case ContractPaymentForm.CREDIT_CARD:
        return <CreditCardIcon sx={{ fontSize: 24, color: 'primary.main' }} />;
      case ContractPaymentForm.BANK_TRANSFER:
        return <SwapHorizIcon sx={{ fontSize: 24, color: 'primary.main' }} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Box
        className={classes.transaction}
        display="flex"
        alignItems="center"
        sx={{ position: 'relative', height: '100%' }}
      >
        <Box
          className={classes.transactionIcon}
          sx={{
            alignSelf: 'center',
            flexShrink: 0
          }}
        >
          {getPaymentMethodIconComponent()}
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            style={{ marginBottom: 10 }}
            color="textPrimary"
            component="p"
            variant="body2"
          >
            {t('CONTRACT_VIEW.CONTRACT_DETAIL.PAYMENT_METHOD')}
          </Typography>
          <Typography
            color={paymentForm ? 'primary' : 'textSecondary'}
            component="h2"
            variant="h2"
            noWrap
          >
            {getPaymentMethodDisplayText(paymentForm, t)}
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 0.5
          }}
        >
          {showDetailsButton && (
            <IconButton
              onClick={handleOpenDetailsDialog}
              color="primary"
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
          )}
          {isEditable && (
            <IconButton
              onClick={handleOpenEditDialog}
              color="primary"
              size="small"
            >
              <Edit />
            </IconButton>
          )}
        </Box>
      </Box>

      <PaymentMethodDetailsDialog
        open={openDetailsDialog}
        onClose={handleCloseDetailsDialog}
        paymentForm={paymentForm}
        selectedBankAccount={selectedBankAccount}
        selectedSecondaryBankAccount={selectedSecondaryBankAccount}
        selectedCard={selectedCard}
        clabe={contract?.clabe}
      />

      <PaymentMethodEditDialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        onSave={handleSave}
        isEditable={isEditable}
        isSaving={isSaving}
        canSave={canSave}
        currentPaymentForm={paymentForm}
        hasPendingAutomaticDebits={hasPendingAutomaticDebits}
        dialogPaymentForm={dialogPaymentForm}
        dialogBankAccountId={dialogBankAccountId}
        dialogSecondaryBankAccountId={dialogSecondaryBankAccountId}
        dialogCardId={dialogCardId}
        onPaymentFormChange={handleDialogPaymentFormChange}
        onBankAccountChange={handleDialogBankAccountChange}
        onSecondaryBankAccountChange={handleDialogSecondaryBankAccountChange}
        onCardChange={handleDialogCardChange}
        validBankAccounts={validBankAccounts}
        creditCards={creditCards}
        loadingCards={loadingCards}
        clabe={contract?.clabe}
      />
    </>
  );
};

PaymentMethodSelector.propTypes = {
  contract: PropTypes.object.isRequired
};

export default PaymentMethodSelector;
